/**
 * Limits for uploading from the user's computer (Upload Files, Upload Folder, drag and drop).
 *
 * Without them a dropped folder of 20,000 small files started 20,000 uploads at once, each with
 * its own toast and Transfers entry, and the page stalled. Bigger sets belong in a zip.
 */

/** Most files one upload (a drop, a picked folder, a file selection) may hold. */
export const MAX_FILES_PER_UPLOAD = 1000

/** Files sent at the same time; the rest wait their turn. */
export const UPLOAD_CONCURRENCY = 4

export class TooManyFilesError extends Error {
  /**
   * @param count files counted; with `exact` false, counting stopped there (there are more).
   */
  constructor(readonly count: number, readonly exact: boolean) {
    super(tooManyFilesMessage(count, exact))
    this.name = 'TooManyFilesError'
  }
}

export function tooManyFilesMessage(count: number, exact: boolean): string {
  const amount = exact ? count.toLocaleString() : `more than ${MAX_FILES_PER_UPLOAD.toLocaleString()}`
  return (
    `This upload has ${amount} files. Upload at most ${MAX_FILES_PER_UPLOAD.toLocaleString()} files ` +
    'at a time, or zip the folder and upload the zip.'
  )
}

/** Throws TooManyFilesError when `count` files are more than one upload may hold. */
export function assertUploadSize(count: number): void {
  if (count > MAX_FILES_PER_UPLOAD) throw new TooManyFilesError(count, true)
}

/**
 * Runs the tasks with at most `limit` at a time, in order. One failure doesn't stop the rest:
 * every task runs, then the first error (if any) is thrown, like Promise.all would have.
 */
export async function runLimited<T>(tasks: Array<() => Promise<T>>, limit: number = UPLOAD_CONCURRENCY): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let firstError: {error: unknown} | undefined
  let next = 0
  const worker = async () => {
    while (next < tasks.length) {
      const index = next++
      try {
        results[index] = await tasks[index]()
      } catch (error: unknown) {
        firstError = firstError ?? {error}
      }
    }
  }
  await Promise.all(Array.from({length: Math.min(limit, tasks.length)}, worker))
  if (firstError) throw firstError.error
  return results
}
